import React, { useState, useEffect } from "react";
import { Sparkles, HelpCircle, AlertTriangle, CheckCircle, Flag, ArrowRight } from "lucide-react";

export default function SmartClarifyingPrompt({
  category,
  amount = 0,
  taxParameters = {},
  value = {},
  onChange,
  onOpenImprovementModal,
}) {
  const [selectedOptionKey, setSelectedOptionKey] = useState("");
  const [customPct, setCustomPct] = useState(value.deductible_pct ?? 100);
  const [showCustomSlider, setShowCustomSlider] = useState(false);
  const [manualCpaFlag, setManualCpaFlag] = useState(Boolean(value.needs_cpa_review));
  const [manualCpaReason, setManualCpaReason] = useState(value.cpa_review_reason || "");

  const mealsPct = taxParameters.business_meals_deductible_pct ?? 50;

  // Sync state if category changes
  useEffect(() => {
    if (category === "Mortgage Interest" && value.principal_amount > 0) {
      setSelectedOptionKey("mortgage_statement_split");
    } else {
      setSelectedOptionKey("");
    }
    setShowCustomSlider(false);
  }, [category, value.principal_amount]);

  const handleSelectOption = (opt) => {
    setSelectedOptionKey(opt.key);
    const newValues = {
      deductible_pct: opt.pct,
      deductibility_reason: opt.reason,
      needs_cpa_review: opt.cpaReview ?? false,
      cpa_review_reason: opt.cpaReason ?? null,
      is_capital_improvement: opt.isCapital ?? false,
    };
    setCustomPct(opt.pct);
    setManualCpaFlag(opt.cpaReview ?? false);
    setManualCpaReason(opt.cpaReason || "");
    setShowCustomSlider(opt.showSlider ?? false);
    onChange?.(newValues);

    if (opt.isCapital && onOpenImprovementModal) {
      // Prompt user or offer direct addition to capital improvements tracker
    }
  };

  const handleSliderChange = (newPct) => {
    setCustomPct(newPct);
    onChange?.({
      deductible_pct: newPct,
      deductibility_reason: `Custom ${newPct}% business use allocation`,
      needs_cpa_review: manualCpaFlag,
      cpa_review_reason: manualCpaReason || "Mixed personal/business allocation",
    });
  };

  const handleToggleCpa = (checked) => {
    setManualCpaFlag(checked);
    onChange?.({
      needs_cpa_review: checked,
      cpa_review_reason: checked ? (manualCpaReason || "Flagged for CPA review") : null,
    });
  };

  const handleCpaReasonChange = (reason) => {
    setManualCpaReason(reason);
    onChange?.({
      cpa_review_reason: reason,
    });
  };

  // Configure clarifying questions by category
  let promptData = null;

  if (category === "Meals & Entertainment") {
    promptData = {
      title: "Clarify Business Meal Purpose",
      question: "What was the business purpose of this meal or dining expense?",
      options: [
        {
          key: "meals_business",
          label: `Dining with contractor, tenant, or business partner (${mealsPct}% deductible)`,
          description: `Qualifies for standard ${mealsPct}% business meal deduction under IRC § 274(n).`,
          pct: mealsPct,
          reason: `${mealsPct}% business meal under IRC § 274(n)`,
          cpaReview: false,
        },
        {
          key: "meals_personal",
          label: "Personal meal during property visit (0% deductible)",
          description: "Solo meals on day trips without overnight stay are considered personal living expenses by the IRS.",
          pct: 0,
          reason: "Personal living expense under IRC § 262 (0% deductible)",
          cpaReview: false,
        },
        {
          key: "meals_entertainment",
          label: "Entertainment tickets / sporting event (0% deductible)",
          description: "Entertainment and sporting event tickets are strictly 0% deductible under TCJA.",
          pct: 0,
          reason: "Nondeductible entertainment under IRC § 274(a)",
          cpaReview: false,
        },
      ],
    };
  } else if (category === "Repairs") {
    promptData = {
      title: "Repair vs. Capital Improvement Check",
      question: "Does this work restore the property to normal condition, or does it upgrade/extend useful life?",
      options: [
        {
          key: "repairs_routine",
          label: "Routine Repair (100% deductible today)",
          description: "Fixing a leak, servicing existing furnace, patching drywall, or replacing broken hardware.",
          pct: 100,
          isCapital: false,
          reason: "Routine maintenance and repairs under IRC § 162",
          cpaReview: false,
        },
        {
          key: "repairs_capital",
          label: "Major Capital Improvement (Depreciated over 27.5 years)",
          description: "Full roof replacement, new HVAC system, complete bathroom/kitchen remodel, or structural addition.",
          pct: 0,
          isCapital: true,
          reason: "Capital improvement depreciated over 27.5 yrs under IRC § 168 (Form 4562)",
          cpaReview: false,
        },
        {
          key: "repairs_unsure",
          label: "Significant project over $2,500 — Unsure (Flag for CPA Review)",
          description: "Let your CPA decide whether it qualifies under De Minimis Safe Harbor or must be capitalized.",
          pct: 100,
          isCapital: false,
          reason: "Pending CPA review: Repair vs Capitalization Treas. Reg. § 1.263(a)-3",
          cpaReview: true,
          cpaReason: "Determine if project qualifies under $2,500 De Minimis safe harbor or requires capitalization",
        },
      ],
    };
  } else if (category === "Auto & Travel") {
    promptData = {
      title: "Travel & Vehicle Type",
      question: "What kind of travel was this expense for?",
      options: [
        {
          key: "travel_lodging",
          label: "Out-of-town lodging, flights, or rental car (100% deductible)",
          description: "Overnight travel solely dedicated to inspecting or managing rental properties.",
          pct: 100,
          reason: "100% long-distance rental travel under IRC § 162",
          cpaReview: false,
        },
        {
          key: "travel_mixed",
          label: "Mixed travel (Personal vacation + Rental inspection)",
          description: "Only the direct business days/activities are deductible. Set your business percentage below.",
          pct: 50,
          showSlider: true,
          reason: "Partial business travel allocation",
          cpaReview: true,
          cpaReason: "Verify business vs personal travel allocation and documentation",
        },
        {
          key: "travel_local",
          label: "Local driving between properties / supply stores",
          description: "Tip: For personal vehicle mileage, use the Mileage Tracker to claim the IRS standard mileage rate!",
          pct: 100,
          reason: "Local vehicle travel under IRC § 162",
          cpaReview: false,
        },
      ],
    };
  } else if (category === "Insurance") {
    promptData = {
      title: "Insurance Policy Classification",
      question: "What type of policy coverage is this?",
      options: [
        {
          key: "ins_standard",
          label: "Annual landlord dwelling / liability policy (100% deductible)",
          description: "Standard policy covering 100% rental properties during the tax year.",
          pct: 100,
          reason: "100% rental property insurance under IRC § 162",
          cpaReview: false,
        },
        {
          key: "ins_multiyear",
          label: "Multi-year policy paid in advance (Needs CPA Amortization)",
          description: "Prepaid insurance for future years must be spread across policy years rather than deducted in full today.",
          pct: 100,
          reason: "Prepaid multi-year insurance (amortization required)",
          cpaReview: true,
          cpaReason: "Amortize multi-year prepaid insurance across applicable tax years",
        },
        {
          key: "ins_mixed",
          label: "Homeowners policy covering personal space + rented unit / ADU",
          description: "Only the square-footage portion dedicated to rental is deductible.",
          pct: 50,
          showSlider: true,
          reason: "Prorated rental portion of homeowners insurance",
          cpaReview: true,
          cpaReason: "Verify square footage proration for shared residence insurance",
        },
      ],
    };
  } else if (category === "Utilities") {
    promptData = {
      title: "Utility Responsibility Check",
      question: "Who is responsible for this utility bill?",
      options: [
        {
          key: "util_owner",
          label: "Owner-paid for 100% rental unit or STR (100% deductible)",
          description: "Utilities paid directly by owner during tenant tenancy, turnover, or for STR guests.",
          pct: 100,
          reason: "100% landlord-paid rental utilities under IRC § 162",
          cpaReview: false,
        },
        {
          key: "util_shared",
          label: "Shared utility bill between rental and personal space",
          description: "Specify the percentage allocated to the rental unit below.",
          pct: 50,
          showSlider: true,
          reason: "Partial rental allocation of shared utility meter",
          cpaReview: true,
          cpaReason: "Verify allocation formula for shared residential utility meter",
        },
      ],
    };
  } else if (category === "Legal & Professional") {
    promptData = {
      title: "Legal & Professional Purpose",
      question: "What service was provided by this legal or financial professional?",
      options: [
        {
          key: "legal_routine",
          label: "Routine operations: lease drafting, eviction, CPA tax prep (100% deductible)",
          description: "Routine business fees are fully deductible in the current tax year.",
          pct: 100,
          reason: "Routine legal/tax advisory fees under IRC § 162",
          cpaReview: false,
        },
        {
          key: "legal_acquisition",
          label: "Purchase title search / property acquisition legal fees (Capitalized)",
          description: "Legal fees spent to purchase property must be added to your property basis, not expensed on Schedule E.",
          pct: 0,
          reason: "Acquisition legal costs capitalized into property purchase basis under IRC § 263",
          cpaReview: true,
          cpaReason: "Add acquisition legal costs to property tax basis (depreciated over 27.5 yrs)",
        },
        {
          key: "legal_startup",
          label: "New LLC formation / organizational setup",
          description: "Startup costs may qualify for IRC § 195 election (up to $5,000 expensed, balance amortized over 15 yrs).",
          pct: 100,
          reason: "LLC organizational startup costs under IRC § 195",
          cpaReview: true,
          cpaReason: "Prepare IRC § 195 startup costs deduction and amortization schedule",
        },
      ],
    };
  } else if (category === "Supplies") {
    promptData = {
      title: "Supplies & Safe Harbor Verification",
      question: "What was purchased and what is the invoice amount?",
      options: [
        {
          key: "supplies_safeharbor",
          label: "Consumable items, small tools, or items under $2,500 (100% deductible)",
          description: "Covered under the IRS De Minimis Safe Harbor for tangible property.",
          pct: 100,
          reason: "De Minimis Safe Harbor under Treas. Reg. § 1.263(a)-1(f)",
          cpaReview: false,
        },
        {
          key: "supplies_major",
          label: "Equipment or appliance costing over $2,500",
          description: "Tangible items over $2,500 generally must be capitalized and depreciated over 5 years (or bonus depreciation).",
          pct: 0,
          isCapital: true,
          reason: "Capital equipment over $2,500 (Form 4562 / MACRS)",
          cpaReview: true,
          cpaReason: "Asset over $2,500 requires 5-year MACRS depreciation or bonus depreciation election",
        },
      ],
    };
  } else if (category === "Mortgage Interest") {
    promptData = {
      title: "Mortgage Payment Verification",
      question: "Does this amount represent ONLY interest, or principal as well?",
      options: [
        ...(value.principal_amount > 0 ? [{
          key: "mortgage_statement_split",
          label: `Statement Auto-Split: Principal $${Number(value.principal_amount).toFixed(2)} excluded (${Number(value.deductible_pct || 0).toFixed(0)}% deductible)`,
          description: `Schedule E deductible: $${Number(value.deductible_amount || 0).toFixed(2)} (Interest + Escrow/Taxes). Non-deductible loan principal is excluded.`,
          pct: value.deductible_pct,
          reason: value.deductibility_reason || `Principal of $${Number(value.principal_amount).toFixed(2)} is non-deductible; balance is Schedule E deductible.`,
          cpaReview: false,
        }] : []),
        {
          key: "mortgage_interest_only",
          label: "Interest portion only (100% deductible)",
          description: "Directly matches the mortgage interest line on your monthly bank statement or Form 1098.",
          pct: 100,
          reason: "Mortgage interest under IRC § 163 (Form 1098 Box 1)",
          cpaReview: false,
        },
        {
          key: "mortgage_full_payment",
          label: "Full monthly check (Principal + Interest combined)",
          description: "Important: Loan principal is NOT deductible! Only enter the interest portion here, or flag for your CPA to split.",
          pct: 50,
          reason: "Principal & interest combined — requires split before Schedule E filing",
          cpaReview: true,
          cpaReason: "Split monthly mortgage payment between deductible interest and balance-sheet principal",
        },
      ],
    };
  } else if (["Capital Contribution", "Owner Draw", "Due to Member"].includes(category)) {
    return (
      <div className="border border-purple-200 dark:border-purple-900/40 bg-purple-50/70 dark:bg-purple-950/20 rounded-xl p-3.5 text-xs text-purple-900 dark:text-purple-200">
        <div className="flex items-center gap-2 font-bold text-sm text-purple-800 dark:text-purple-300">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <span>Non-Deductible Equity / Balance Sheet Item</span>
        </div>
        <p className="mt-1.5 text-slate-700 dark:text-slate-300 leading-relaxed text-[11.5px]">
          <strong>{category}</strong> is an equity or loan transfer on your balance sheet, NOT a rental expense on IRS Schedule E.
          It will be recorded with <strong>0% tax deductibility</strong> so it won&apos;t inflate your deductible expenses.
        </p>
      </div>
    );
  }

  if (!promptData) return null;

  return (
    <div className="border border-amber-200 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/20 rounded-xl p-3.5 space-y-3 text-xs text-slate-800 dark:text-slate-200">
      <div className="flex items-center gap-2">
        <span className="p-1 rounded-md bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300">
          <Sparkles className="w-4 h-4" />
        </span>
        <div>
          <h4 className="font-bold text-slate-900 dark:text-slate-100 text-sm">
            {promptData.title}
          </h4>
          <p className="text-slate-600 dark:text-slate-400 text-[11px]">
            {promptData.question}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {promptData.options.map((opt) => {
          const isSelected = selectedOptionKey === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => handleSelectOption(opt)}
              className={`w-full text-left p-2.5 rounded-lg border transition-all text-xs ${
                isSelected
                  ? "border-blue-600 bg-blue-50 dark:bg-blue-950/50 dark:border-blue-500 shadow-sm"
                  : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {opt.label}
                </span>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                  {opt.pct}%
                </span>
              </div>
              <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-400 leading-snug">
                {opt.description}
              </p>
            </button>
          );
        })}
      </div>

      {/* Capital Improvement notice if selected */}
      {value.is_capital_improvement && (
        <div className="p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900/50 text-indigo-900 dark:text-indigo-200 text-xs">
          <div className="font-bold flex items-center gap-1.5 text-indigo-800 dark:text-indigo-300">
            <CheckCircle className="w-3.5 h-3.5 text-indigo-600" />
            <span>Marked as Capital Improvement</span>
          </div>
          <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-400">
            This expense cannot be written off all at once on Schedule E Line 14. It will be added to your property&apos;s depreciation schedule (27.5 years).
          </p>
          {onOpenImprovementModal && (
            <button
              type="button"
              onClick={onOpenImprovementModal}
              className="mt-2 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
            >
              <span>Add to Capital Improvements Tracker now</span>
              <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* Custom percentage slider */}
      {showCustomSlider && (
        <div className="pt-2 border-t border-amber-200 dark:border-amber-900/30 space-y-2">
          <div className="flex items-center justify-between text-xs font-semibold">
            <span>Deductible Percentage:</span>
            <span className="font-bold text-blue-600 dark:text-blue-400 text-sm">
              {customPct}%
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={customPct}
            onChange={(e) => handleSliderChange(Number(e.target.value))}
            className="w-full accent-blue-600 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-slate-500">
            <span>0% (Non-deductible)</span>
            <span>50% (Standard mixed)</span>
            <span>100% (Fully deductible)</span>
          </div>
        </div>
      )}

      {/* Manual CPA Review toggle */}
      <div className="pt-2 border-t border-amber-200 dark:border-amber-900/30 space-y-2">
        <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-800 dark:text-slate-200">
          <input
            type="checkbox"
            checked={manualCpaFlag}
            onChange={(e) => handleToggleCpa(e.target.checked)}
            className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
          />
          <Flag className={`w-3.5 h-3.5 ${manualCpaFlag ? "text-amber-600 fill-amber-600" : "text-slate-400"}`} />
          <span>Flag this transaction for CPA Review</span>
        </label>

        {manualCpaFlag && (
          <input
            type="text"
            value={manualCpaReason}
            onChange={(e) => handleCpaReasonChange(e.target.value)}
            placeholder="Reason for CPA review (e.g. verify repair vs capitalization)"
            className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-amber-300 dark:border-amber-800 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 placeholder-slate-400"
          />
        )}
      </div>
    </div>
  );
}
